import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { useTable, usePagination } from "react-table";

import Spinner from "../../../Components/common/Spinner/Spinner";
import { RequestMailGatewayLogs, RequestPefLogs } from "../../../store/actions/zsearchActions";

import "./styles.scss";

const useQuery = () => new URLSearchParams(useLocation().search);

const LogWindow = () => {
  const dispatch = useDispatch();
  const { type } = useParams();
  const query = useQuery();

  const mgwLogs = useSelector((state) => state.zsearch.mgwLogs);
  const mgwLogsLoading = useSelector((state) => state.zsearch.mgwLogsLoading);
  const pefLogs = useSelector((state) => state.zsearch.pefLogs);
  const pefLogsLoading = useSelector((state) => state.zsearch.pefLogsLoading);
  const loading = mgwLogsLoading || pefLogsLoading;

  const [hiddenColumns, setHiddenColumns] = useState(["message_id","host","epoch","pid","@timestamp","device","deliver_epoch","delay"]);
  const disabledColumns = ["host","epoch","pid","@timestamp","device","deliver_epoch","delay"];
  const [filterText, setFilterText] = useState(""); // State for filter text

  // Fetch Logs when params change
  const fetchLogs = useCallback(() => {
    const index = query.get("index");
    const size = Number(query.get("size")) || 10;
    const timeRange = query.get("time-range");
    const keyword = query.get("keyword");

    if (type === "mgw") {
      const process = query.get("process");
      dispatch(RequestMailGatewayLogs({ index, size, timeRange, keyword, process }));
    } else if (type === "pef") {
      const device = query.get("device");
      dispatch(RequestPefLogs({ index, size, timeRange, keyword, device }));
    }
  }, [dispatch, type]);

  // Only fetch when type or query changes (avoid unnecessary re-fetches)
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Avoid unnecessary state updates by computing table data dynamically
  const tableData = useMemo(
    () => (type === "mgw" ? mgwLogs : pefLogs) || [],
    [type, mgwLogs, pefLogs],
  );

  // Define columns dynamically based on the data structure
  const columns = useMemo(() => {
    console.log("tableData", tableData);
    console.log("tableData.length", tableData.length);
    if (!Array.isArray(tableData) || tableData.length === 0) return [];
    return Object.keys(tableData[0]).map((key) => ({
      Header: key,
      accessor: key,
    }));
  }, [tableData]);

  // Filter rows based on filterText input
  const filteredData = useMemo(() => {
    if (!filterText) return tableData;
    return tableData.filter((row) => {
      return Object.values(row).some((val) =>
        String(val).toLowerCase().includes(filterText.toLowerCase()),
      );
    });
  }, [tableData, filterText]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
    setHiddenColumns: setTableHiddenColumns, // Ensure table state updates correctly
  } = useTable(
    {
      columns,
      data: filteredData, // Use filtered data here
      initialState: { pageIndex: 0, pageSize: 10, hiddenColumns },
    },
    usePagination,
  );

  // Sync hiddenColumns state with table state
  useEffect(() => {
    setTableHiddenColumns(hiddenColumns);
  }, [hiddenColumns, setTableHiddenColumns]);

  // Toggle column visibility
  const toggleColumn = (columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId) ? prev.filter((col) => col !== columnId) : [...prev, columnId],
    );
  };

  // Handle direct page change input
  const handlePageChange = (e) => {
    const page = e.target.value ? Number(e.target.value) - 1 : 0;
    if (page >= 0 && page < pageCount) {
      gotoPage(page);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
  };

  const handleRefresh = () => {
    fetchLogs();
  };

  return (
    <div className="log-window">
      {loading ? (
        <div className="log-window__center">
          <Spinner width={24} height={24} />
        </div>
      ) : tableData.length > 0 ? (
        <div>
          {/* Column Toggle Buttons */}
          <div className="buttons">
            {columns.map((col, i) => {
              // Check if the column is in disabledColumns
              const isDisabled = disabledColumns.includes(col.accessor);

              return (
                <button
                  key={i}
                  onClick={() => toggleColumn(col.accessor)}
                  className={
                    hiddenColumns.includes(col.accessor)
                      ? "button button--faded"
                      : "button button--highlighted"
                  }
                  style={{ display: isDisabled ? "none" : "inline-block" }} // Hide button if disabled
                >
                  {hiddenColumns.includes(col.accessor) ? "Show" : "Hide"}: {col.Header}
                </button>
              );
            })}
            <button className="button" style={{ marginLeft: "auto" }} onClick={handleRefresh}>
              Refresh
            </button>
          </div>

          {/* Filter Input Field */}
          <div className="filter">
            <input
              type="text"
              placeholder="Search in all columns"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="filter-input"
            />
          </div>

          {/* Table */}
          <table {...getTableProps()} className="log-table">
            <thead>
              {headerGroups.map((headerGroup, i) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                  {headerGroup.headers.map((column, idx) =>
                    !hiddenColumns.includes(column.id) ? (
                      <th {...column.getHeaderProps()} key={idx}>
                        {column.render("Header")}
                      </th>
                    ) : null,
                  )}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={i}>
                    {row.cells.map((cell) =>
                      !hiddenColumns.includes(cell.column.id) ? (
                        <td {...cell.getCellProps()} key={cell.column.id + row.id}>
                          {cell.render("Cell")}
                        </td>
                      ) : null,
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="pagination">
            <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
              {"<<"}
            </button>
            <button onClick={previousPage} disabled={!canPreviousPage}>
              {"<"}
            </button>

            <span>
              Go to page: <input type="number" value={pageIndex + 1} onChange={handlePageChange} />
            </span>

            <span>
              {pageIndex + 1} of {pageCount} pages
            </span>

            <button onClick={nextPage} disabled={!canNextPage}>
              {">"}
            </button>
            <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
              {">>"}
            </button>

            <select value={pageSize} onChange={handlePageSizeChange}>
              {[10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <div className="log-window__center">No log data with current filters.</div>
      )}
    </div>
  );
};

export default LogWindow;
